import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'
import { useContext, useMemo } from 'react'

import EmployerContext from 'components/context/EmployerContext'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Badge } from 'slab'
import { useGetAdminEmployersEmployerIdPlaidAccount } from 'utils/web_api'
import { type AdminPlaidAccount } from 'utils/web_api.schemas'

const columnHelper = createColumnHelper<AdminPlaidAccount>()

const createColumns = () => [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('mask', {
    header: 'Last 4',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('institution_name', {
    header: 'Institution',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('verification_status', {
    header: 'Verification',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.display({
    id: 'errors',
    header: 'Errors',
    cell: ({ row }) => {
      const { error_type: errorType, error_code: errorCode } = row.original
      return (
        <span>
          {errorType} - {errorCode}
        </span>
      )
    },
  }),
  columnHelper.accessor('item_id', {
    header: 'Item ID',
    cell: ({ getValue }) => {
      const itemId = getValue()
      return (
        <Badge
          type='info'
          actionIcon={ExternalLink}
          onActionClick={() => {
            window.open(`https://dashboard.plaid.com/activity/debugger/${itemId}`, '_blank')
          }}
        >
          {itemId.slice(-6)}
        </Badge>
      )
    },
  }),
]

const EmployerPlaidAccountTable = () => {
  const { employer } = useContext(EmployerContext)

  const { data: plaidAccount, isLoading, error } = useGetAdminEmployersEmployerIdPlaidAccount(
    employer!.id,
    { query: { enabled: !!employer && employer!.has_plaid_account } }
  )

  // Convert single plaid account to array for TanstackTable
  const plaidAccounts = useMemo(() => {
    return plaidAccount ? [plaidAccount] : []
  }, [plaidAccount])

  const columns = useMemo(() => createColumns(), [])

  const table = useReactTable({
    data: plaidAccounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={!!error}
      noDataMessage='No Plaid account connected for this employer'
      tableMessageHeightClass='h-14'
      paginationLabelSingular='Plaid account'
      paginationLabelPlural='Plaid accounts'
      loadingSkeletonPageSizeOverride={1}
    />
  )
}

export default EmployerPlaidAccountTable
